import React, { useEffect, useMemo, useState } from 'react';
import type { SIFTVehicle } from '../../types/sift';
import type { BrokerBuyerMatch } from '../../api/types';
import { BuyerMatchPanel } from './BuyerMatchPanel';
import { BrokerVinStatusControl } from './BrokerVinStatusControl';
import {
  PRIORITY_COLOR, PRIORITY_LABEL,
  PRICE_COLOR, PRICE_LABEL,
  vinSeverityScore, vinSeverityKey, vinSeverityParts, vinSeverityTooltip, vinSeverityPercent,
  vinPriceRatio, vinPriceBucketIndex, priceKeyFromIndex, vinAskingPrice,
  fmtCrGrade, fmtMileage, fmtValuationSource,
} from './brokerViewTokens';
import { VMA_FIELD_HELP, VMA_PANEL_NOTE_BROKER } from '../shared/vmaFieldHelp';
import { VinCohortInsightsCard } from '../shared/VinCohortInsightsCard';
import { useVinCohortInsights } from '../shared/useVinCohortInsights';
import { DaysBars } from '../shared/DaysBars';
import { LevelBadge, VelocityCaption } from '../shared/VmaLevels';
import {
  WS_SUPPLY_TONE, WS_DEMAND_TONE, RETAIL_SUPPLY_TONE, RETAIL_DEMAND_TONE,
} from '../shared/vmaLevelTones';
import './VinDetailsPopout.css';
import { pushUsageContext, track } from '../../telemetry/usage';

interface VinDetailsPopoutProps {
  vehicle: SIFTVehicle | null;
  isLoading: boolean;
  onClose: () => void;
  onBack?: () => void;        // optional "back to dealer" affordance
  backLabel?: string;
  /** When provided, the buyer-match panel shows an "Add to basket" action. */
  onAddBuyerToBasket?: (buyer: BrokerBuyerMatch, vin: string) => void;
  /** Stage several buyers on this VIN in one write ("Add all" on the engaged buyers). */
  onAddBuyersToBasket?: (buyers: BrokerBuyerMatch[], vin: string) => void;
  /** buyer_uid values already in the basket for this VIN. */
  buyerBasketKeys?: Set<string>;
  /** Undo a buyer add for this VIN (the ✓ toggles back to +). */
  onRemoveBuyerFromBasket?: (buyerUid: string, vin: string) => void;
  /** §14.17: click a recommended buyer to jump to that buyer on the Buyer tab. */
  onFocusBuyer?: (buyerUid: string, buyerName?: string | null) => void;
  /** Jump to this VIN's cluster on the Cluster tab (the other comparable units). */
  onOpenCluster?: (vin: string) => void;
  /** §6.5: keep the carousel row in step when the status is changed from here. */
  onStatusChanged?: (vin: string, status: string | null) => void;
}

// ---------- formatters ----------
const fmtMoney = (n?: number | null) =>
  n == null ? '–' : `$${Math.round(n).toLocaleString()}`;
const fmtInt = (n?: number | null) =>
  n == null ? '–' : Math.round(n).toLocaleString();
const fmtStr = (v?: string | number | null) =>
  v == null || v === '' ? '–' : String(v);

// CAFS is MMI, ECR is not — spell it out so the raw code isn't read as a vendor name.
const fmtInspectionSource = (v?: string | null) => {
  const s = (v ?? '').trim().toUpperCase();
  if (!s) return '–';
  if (s === 'CAFS' || s === 'CAFS LIVE') return `MMI (${s})`;
  if (s === 'ECR') return 'ECR (not MMI)';
  return s;
};

const fmtDate = (raw?: string | null) => {
  if (!raw || !raw.trim()) return '–';
  const s = raw.trim();
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const cleaned = s.replace(' UTC', '');
    const d = new Date(cleaned + (cleaned.endsWith('Z') ? '' : 'Z'));
    if (isNaN(d.getTime())) return s;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return s;
  }
};

// ---------- small helpers ----------
const Kv: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <div className="vin-kv">
    <span className={`vin-kv-label${hint ? ' vin-kv-label-help' : ''}`} title={hint}>
      {label}
    </span>
    <span className="vin-kv-value">{value}</span>
  </div>
);

const SummaryChip: React.FC<{ label: string; value: React.ReactNode; tooltip?: string }> = ({
  label,
  value,
  tooltip,
}) => (
  <div className="vin-chip" title={tooltip}>
    <span className="vin-chip-label">{label}</span>
    <span className="vin-chip-value">{value}</span>
  </div>
);

const Card: React.FC<{ title: string; className?: string; children: React.ReactNode }> = ({
  title,
  className,
  children,
}) => (
  <section className={`vin-card${className ? ' ' + className : ''}`}>
    <h3 className="vin-card-title">{title}</h3>
    {children}
  </section>
);

const Expander: React.FC<{ label: string; defaultOpen?: boolean; children: React.ReactNode }> = ({
  label,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="vin-exp">
      <button
        type="button"
        className="vin-exp-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {open ? '▾' : '▸'} {label}
      </button>
      {open && <div className="vin-exp-body">{children}</div>}
    </div>
  );
};

// ---------- sortable table primitive ----------
type SortDir = 'asc' | 'desc';
interface SortEntry { key: string; dir: SortDir }

interface TableColumn<T> {
  key: string;
  label: string;
  sortValue: (row: T) => string | number | null | undefined;
  render: (row: T) => React.ReactNode;
  numeric?: boolean;
  /** Long free text (seller, auction, buyer): clip with an ellipsis, full value on hover. */
  truncate?: boolean;
}

function useSort(initial: SortEntry[] = []) {
  const [sortBy, setSortBy] = useState<SortEntry[]>(initial);
  const handleSortClick = (key: string, e: React.MouseEvent) => {
    const additive = e.shiftKey;
    setSortBy((prev) => {
      const idx = prev.findIndex((s) => s.key === key);
      if (additive) {
        if (idx === -1) return [...prev, { key, dir: 'asc' }];
        const existing = prev[idx];
        if (existing.dir === 'asc') {
          const next = [...prev];
          next[idx] = { key, dir: 'desc' };
          return next;
        }
        return prev.filter((_, i) => i !== idx);
      }
      if (idx !== -1 && prev.length === 1) {
        const e2 = prev[idx];
        if (e2.dir === 'asc') return [{ key, dir: 'desc' }];
        return [];
      }
      return [{ key, dir: 'asc' }];
    });
  };
  return { sortBy, handleSortClick };
}

function compareValues(a: any, b: any, dir: SortDir): number {
  const aNull = a == null || (typeof a === 'number' && Number.isNaN(a));
  const bNull = b == null || (typeof b === 'number' && Number.isNaN(b));
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  let cmp = 0;
  if (typeof a === 'number' && typeof b === 'number') cmp = a - b;
  else cmp = String(a).localeCompare(String(b));
  return dir === 'asc' ? cmp : -cmp;
}

interface SortableTableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  searchPlaceholder?: string;
  searchableKeys?: (keyof T | string)[];
  rowKey: (row: T, idx: number) => string;
  emptyText?: string;
}

function SortableTable<T extends Record<string, any>>({
  columns: allColumns,
  rows,
  searchPlaceholder,
  rowKey,
  emptyText = 'No data',
}: SortableTableProps<T>) {
  const [query, setQuery] = useState('');
  const { sortBy, handleSortClick } = useSort();

  // A column with no value on any row is noise that pushes the useful ones out of
  // view: the listing feed never carries adj_mmr and almost never a sale/buyer.
  const columns = useMemo(() => {
    if (rows.length === 0) return allColumns;
    const kept = allColumns.filter((c) =>
      rows.some((r) => {
        const v = c.sortValue(r);
        return v != null && v !== '' && !(typeof v === 'number' && Number.isNaN(v));
      }),
    );
    return kept.length > 0 ? kept : allColumns;
  }, [allColumns, rows]);

  const filtered = useMemo(() => {
    let arr = rows;
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      arr = arr.filter((r) =>
        columns.some((c) => {
          const v = c.sortValue(r);
          return v != null && String(v).toLowerCase().includes(q);
        }),
      );
    }
    if (sortBy.length > 0) {
      const colMap = new Map(columns.map((c) => [c.key, c]));
      arr = [...arr].sort((a, b) => {
        for (const s of sortBy) {
          const col = colMap.get(s.key);
          if (!col) continue;
          const cmp = compareValues(col.sortValue(a), col.sortValue(b), s.dir);
          if (cmp !== 0) return cmp;
        }
        return 0;
      });
    }
    return arr;
  }, [rows, query, sortBy, columns]);

  return (
    <div className="vin-table-wrap">
      {searchPlaceholder && (
        <input
          type="text"
          className="vin-table-search"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}
      <div className="vin-table-scroll">
        <table className="vin-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const idx = sortBy.findIndex((s) => s.key === col.key);
                const entry = idx !== -1 ? sortBy[idx] : null;
                const arrow = entry ? (entry.dir === 'asc' ? '▲' : '▼') : '';
                return (
                  <th
                    key={col.key}
                    className={`vin-th${entry ? ' vin-th-sorted' : ''}${col.numeric ? ' vin-th-num' : ''}`}
                    onClick={(e) => handleSortClick(col.key, e)}
                    title="Click to sort. Shift+click for multi-sort."
                  >
                    <span>{col.label}</span>
                    {entry && (
                      <span className="vin-th-sort">
                        {arrow}
                        {sortBy.length > 1 && <sup>{idx + 1}</sup>}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="vin-table-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              filtered.map((r, i) => (
                <tr key={rowKey(r, i)}>
                  {columns.map((c) => {
                    const cls = [c.numeric ? 'vin-td-num' : '', c.truncate ? 'vin-td-trunc' : ''].filter(Boolean).join(' ');
                    const full = c.truncate ? c.sortValue(r) : null;
                    return (
                      <td
                        key={c.key}
                        className={cls || undefined}
                        title={full != null && full !== '' ? String(full) : undefined}
                      >
                        {c.render(r)}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- main component ----------
export const VinDetailsPopout: React.FC<VinDetailsPopoutProps> = ({
  vehicle,
  isLoading,
  onClose,
  onBack,
  backLabel = 'Back',
  onAddBuyerToBasket,
  onAddBuyersToBasket,
  buyerBasketKeys,
  onRemoveBuyerFromBasket,
  onFocusBuyer,
  onOpenCluster,
  onStatusChanged,
}) => {
  const trackedVin = vehicle?.vin;
  const trackedSeller = vehicle?.seller_id;
  useEffect(() => {
    if (!trackedVin) return undefined;
    track('broker_view', 'vin_details', 'open', { vin: trackedVin, seller_uid: trackedSeller });
    return pushUsageContext('vin_details', { vin: trackedVin, seller_uid: trackedSeller });
  }, [trackedVin, trackedSeller]);
  // The upstream split is best-effort and often absent, so treat the fused text as
  // the source of truth and only show the parts when they actually add something.
  const annc = useMemo(() => {
    const raw = vehicle?.recent_announcements;
    const full = (Array.isArray(raw) ? raw.filter(Boolean).join(' ') : String(raw ?? '')).trim();
    const clean = (v?: string | null) => {
      const s = (v ?? '').trim();
      return s && /[a-z]/i.test(s) ? s : '';
    };
    const structured = clean(vehicle?.structured_announcements);
    const remarks = clean(vehicle?.remarks);
    const dealer = clean(vehicle?.seller_comments);
    return { full, structured, remarks, dealer, split: Boolean(structured || remarks || dealer) };
  }, [vehicle?.recent_announcements, vehicle?.structured_announcements, vehicle?.remarks, vehicle?.seller_comments]);

  // ---------- table column definitions ----------
  const listingHistoryCols: TableColumn<any>[] = [
    { key: 'list_time', label: 'List Time', sortValue: (r) => r.list_time, render: (r) => fmtDate(r.list_time) },
    { key: 'list_status', label: 'Status', sortValue: (r) => r.list_status, render: (r) => fmtStr(r.list_status) },
    { key: 'auction', label: 'Auction', truncate: true, sortValue: (r) => r.auction, render: (r) => fmtStr(r.auction) },
    { key: 'channel', label: 'Channel', sortValue: (r) => r.channel, render: (r) => fmtStr(r.channel) },
    { key: 'seller_name', label: 'Seller', truncate: true, sortValue: (r) => r.seller_name, render: (r) => fmtStr(r.seller_name) },
    { key: 'state', label: 'State', sortValue: (r) => r.state, render: (r) => fmtStr(r.state) },
    { key: 'adj_mmr', label: 'Adj MMR', numeric: true, sortValue: (r) => r.adj_mmr, render: (r) => fmtMoney(r.adj_mmr) },
    { key: 'buy_now_price', label: 'Buy Now', numeric: true, sortValue: (r) => r.buy_now_price, render: (r) => fmtMoney(r.buy_now_price) },
    { key: 'floor_price', label: 'Floor', numeric: true, sortValue: (r) => r.floor_price, render: (r) => fmtMoney(r.floor_price) },
    { key: 'mileage', label: 'Mileage', numeric: true, sortValue: (r) => r.mileage, render: (r) => fmtMileage(r.mileage) },
    { key: 'cr_grade', label: 'CR', sortValue: (r) => r.cr_grade, render: (r) => fmtCrGrade(r.cr_grade) },
    { key: 'sold_amt', label: 'Sold Amt', numeric: true, sortValue: (r) => r.sold_amt, render: (r) => fmtMoney(r.sold_amt) },
    { key: 'sold_time', label: 'Sold Time', sortValue: (r) => r.sold_time, render: (r) => fmtDate(r.sold_time) },
    { key: 'buyer_name', label: 'Buyer', truncate: true, sortValue: (r) => r.buyer_name, render: (r) => fmtStr(r.buyer_name) },
  ];

  const bidHistoryCols: TableColumn<any>[] = [
    { key: 'bid_time', label: 'Bid Time', sortValue: (r) => r.bid_time, render: (r) => fmtDate(r.bid_time) },
    { key: 'bid_offer_type', label: 'Offer Type', sortValue: (r) => r.bid_offer_type, render: (r) => fmtStr(r.bid_offer_type) },
    { key: 'buyer_name', label: 'Buyer', truncate: true, sortValue: (r) => r.buyer_name, render: (r) => fmtStr(r.buyer_name) },
    { key: 'bid_amt', label: 'Amount', numeric: true, sortValue: (r) => r.bid_amt, render: (r) => fmtMoney(r.bid_amt) },
    { key: 'bid_type', label: 'Type', sortValue: (r) => r.bid_type, render: (r) => fmtStr(r.bid_type) },
    { key: 'bid_channel', label: 'Channel', sortValue: (r) => r.bid_channel, render: (r) => fmtStr(r.bid_channel) },
    { key: 'bid_status', label: 'Status', sortValue: (r) => r.bid_status, render: (r) => fmtStr(r.bid_status) },
    { key: 'closed_time', label: 'Closed Time', sortValue: (r) => r.closed_time, render: (r) => fmtDate(r.closed_time) },
  ];

  // ---------- early returns ----------
  // Keep the panel chrome mounted: returning null here strands the user, because the
  // back / close controls disappear while the parent still considers the pop-out open.
  // One fetch feeds both the price ladder and the channel/venue placement card.
  const cohortInsights = useVinCohortInsights(vehicle?.vin ?? '', !!vehicle?.vin);

  if (!vehicle && !isLoading) {
    return (
      <div className="vin-popout-panel vin-popout-panel--broker" role="dialog" aria-label="VIN details">
        <div className="vin-popout-header">
          <div className="vin-popout-title">
            {onBack && (
              <button className="vin-popout-back" onClick={onBack} title={backLabel}>
                ← {backLabel}
              </button>
            )}
            <h2>Vehicle unavailable</h2>
          </div>
          <button className="vin-popout-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="vin-popout-empty">
          <p className="vin-popout-empty-main">Vehicle data is not available for this VIN.</p>
          <p className="vin-popout-empty-sub">
            It may have sold or been removed since this list was last refreshed. Close this
            panel to continue working.
          </p>
        </div>
      </div>
    );
  }

  // Gross opportunity at the lowest buyer entry, not a promised transaction
  // price. Net-of-fees (buy fee / transport / recon) is a later phase.
  const askingPrice = vehicle ? vinAskingPrice(vehicle) : null;
  const grossSpread =
    vehicle && vehicle.retail_value != null && askingPrice != null
      ? vehicle.retail_value - askingPrice
      : null;
  const grossSpreadHelp = grossSpread == null
    ? 'Gross spread unavailable: requires both an estimated retail value and a valid buyer entry price.'
    : 'Estimated retail value minus the lowest buyer entry (Buy Now, floor, seller counter, opening bid, or configured make-offer entry). An opportunity estimate, not a guaranteed accepted price. GROSS — before buy fee, transport & recon.';
  const mmrSource = vehicle
    ? fmtValuationSource(vehicle.adjusted_mmr, vehicle.adj_mmr_source, vehicle.adj_mmr_as_of)
    : '–';
  const retailSource = vehicle
    ? fmtValuationSource(vehicle.retail_value, vehicle.retail_value_source, vehicle.retail_value_as_of)
    : '–';

  // Urgency (severity) breakdown — the exact data points driving the tier.
  const severityScore = vehicle ? vinSeverityScore(vehicle) : 0;
  const severityKey = vinSeverityKey(severityScore);
  const severityParts = vehicle ? vinSeverityParts(vehicle) : [];
  const severityColor = PRIORITY_COLOR[severityKey];

  // Price position — asking vs market (MMR).
  const priceRatio = vehicle ? vinPriceRatio(vehicle) : null;
  const priceKey = priceKeyFromIndex(vinPriceBucketIndex(priceRatio));
  const priceColor = priceKey ? PRICE_COLOR[priceKey] : null;

  const vehicleTitle = vehicle
    ? `${vehicle.model_year ?? ''} ${vehicle.make ?? ''} ${vehicle.model ?? ''}`.trim() || 'Vehicle'
    : 'Loading…';

  return (
    <div className="vin-popout-panel vin-popout-panel--broker" role="dialog" aria-label="VIN details">
      <div className="vin-popout-header">
        <div className="vin-popout-title">
          {onBack && (
            <button className="vin-popout-back" onClick={onBack} title={backLabel} aria-label={backLabel}>
              ←<span className="vin-popout-back-label"> {backLabel}</span>
            </button>
          )}
          <div className="vin-popout-heading">
            <h2 title={vehicleTitle}>{vehicleTitle}</h2>
            {vehicle && <span className="vin-popout-vin">{vehicle.vin}</span>}
          </div>
        </div>
        <div className="vin-popout-header-actions">
          {vehicle?.vin && (
            <BrokerVinStatusControl vin={vehicle.vin} onChanged={onStatusChanged} />
          )}
          {onOpenCluster && vehicle?.vin && (
            <button
              className="vin-popout-cluster-btn"
              onClick={() => onOpenCluster(vehicle.vin)}
              title={`See the other comparable ${[vehicle.model_year, vehicle.make, vehicle.model].filter(Boolean).join(' ')} units on the Cluster tab`}
            >
              ⧉ Similar units
            </button>
          )}
        </div>
        <button className="vin-popout-close" onClick={onClose} aria-label="Close">✕</button>
      </div>

      {isLoading && <div className="vin-popout-loading">Loading vehicle details…</div>}

      {!isLoading && vehicle && (
        <div className="vin-popout-body">
          {/* Sticky decision strip */}
          <div className="vin-summary-strip">
            <div className="vin-chip vin-chip-urgency" title={vinSeverityTooltip(vehicle)}>
              <span className="vin-chip-label">Urgency</span>
              <span className="vin-chip-value" style={{ color: severityColor.fg }}>
                {PRIORITY_LABEL[severityKey]}
              </span>
            </div>
            {priceKey && priceColor && (
              <div
                className="vin-chip vin-chip-urgency"
                title={`${PRICE_LABEL[priceKey]} — buyer entry ${priceRatio != null ? Math.round(priceRatio * 100) : '–'}% of MMR (lowest valid Buy Now, floor, seller counter, opening bid, or configured make-offer entry; adjusted/base MMR)`}
              >
                <span className="vin-chip-label">Price</span>
                <span className="vin-chip-value" style={{ color: priceColor.fg }}>
                  {PRICE_LABEL[priceKey]}
                </span>
              </div>
            )}
            <SummaryChip label="Adj MMR" value={fmtMoney(vehicle.adjusted_mmr)} tooltip={`Adjusted MMR — adjusted for CR / mileage. Source: ${mmrSource}`} />
            <SummaryChip label="Floor" value={fmtMoney(vehicle.floor_price)} tooltip="Seller-set minimum reserve price" />
            <SummaryChip label="Buy Now" value={fmtMoney(vehicle.buy_now_price)} tooltip="Buy Now price set by the seller" />
            {vehicle.lowest_seller_counter != null && vehicle.lowest_seller_counter > 0 && (
              <SummaryChip
                label="Seller Counter"
                value={fmtMoney(vehicle.lowest_seller_counter)}
                tooltip="Lowest seller counter — the seller's true price point. When it's below the asking, the price position uses it (more buyers become viable)."
              />
            )}
            <SummaryChip label="Retail" value={fmtMoney(vehicle.retail_value)} tooltip={`Estimated retail value. Source: ${retailSource}`} />
            <SummaryChip
              label="Gross Spread"
              value={fmtMoney(grossSpread)}
              tooltip={grossSpreadHelp}
            />
            <SummaryChip label="CR" value={fmtCrGrade(vehicle.cr_grade)} tooltip="Condition Report grade (0–5; out-of-range values are bad data → shown as –)" />
            <SummaryChip label="List Days" value={fmtInt(vehicle.list_days)} tooltip="Days listed in current run" />
            <SummaryChip label="Total Bids" value={fmtInt(vehicle.total_bids)} tooltip="Total bids across the current listing" />
          </div>

          {/* Recommended buyers — the core broker workflow: match this VIN to
              its ~5 exactly-right buyers before outreach. */}
          {vehicle.vin && (
            <BuyerMatchPanel
              vin={vehicle.vin}
              onAddToBasket={onAddBuyerToBasket}
              onAddManyToBasket={onAddBuyersToBasket}
              basketKeys={buyerBasketKeys}
              onRemoveFromBasket={onRemoveBuyerFromBasket}
              onFocusBuyer={onFocusBuyer}
            />
          )}

          <div className="vin-grid">
          {/* Why this urgency — the exact signals + points behind the tier. */}
          <Card title="Why this urgency" className="vin-card-wide">
            <div className="vin-sev-summary">
              <span className="vin-sev-tier" style={{ background: severityColor.bg, color: severityColor.fg }}>
                {PRIORITY_LABEL[severityKey]}
              </span>
              <span className="vin-sev-score">Urgency {vinSeverityPercent(severityScore)}%</span>
            </div>
            <div className="vin-sev-parts">
              {severityParts.map((p) => (
                <div key={p.label} className={`vin-sev-part${p.points > 0 ? ' active' : ''}`} title={p.detail}>
                  <span className="vin-sev-part-label">{p.label}</span>
                  <span className="vin-sev-part-value">{p.display ?? fmtInt(p.value)}</span>
                  <span className="vin-sev-part-points">+{p.points}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Pricing & Value */}
          <Card title="Pricing & Value" className="vin-card-wide">
            {vehicle.vin && (
              <VinCohortInsightsCard
                vin={vehicle.vin}
                sections={['price']}
                insights={cohortInsights}
                pricing={{
                  floorPrice: vehicle.floor_price,
                  buyNowPrice: vehicle.buy_now_price,
                  baseMmr: vehicle.base_mmr,
                  adjMmr: vehicle.adjusted_mmr,
                  mmrRange: vehicle.mmr_range,
                  retailValue: vehicle.retail_value,
                  retailValueRange: vehicle.retail_value_range,
                  retailListPrice: vehicle.retail_list_price,
                  retailRmi: vehicle.retail_rmi,
                  retailAvgListPrice: vehicle.retail_avg_list_price,
                  retailSpread: vehicle.retail_spread,
                  retailRetention: vehicle.retail_retention,
                }}
              />
            )}
            <div className="vin-kv-grid">
              <Kv label="Adj MMR" value={fmtMoney(vehicle.adjusted_mmr)} />
              <Kv label="Base MMR" value={fmtMoney(vehicle.base_mmr)} />
              <Kv label="Floor Price" value={fmtMoney(vehicle.floor_price)} />
              <Kv label="Buy Now" value={fmtMoney(vehicle.buy_now_price)} />
              <Kv label="Seller Counter" value={fmtMoney(vehicle.lowest_seller_counter)} />
              <Kv label="MMR Range" value={fmtStr(vehicle.mmr_range)} />
              <Kv label="Retail Value" value={fmtMoney(vehicle.retail_value)} />
              <Kv label="Retail List" value={fmtMoney(vehicle.retail_list_price)} />
              <Kv label="Retail Range" value={fmtStr(vehicle.retail_value_range)} />
              <Kv label="MMR Source" value={mmrSource} hint="Where SIFT's adjusted MMR came from and the date it was last valued. Manheim.com reprices continuously, so its current value can differ." />
              <Kv label="Retail Source" value={retailSource} hint="Where SIFT's retail value came from and the date it was last valued. Manheim.com can show a newer value." />
              <Kv label="Est. Gross Spread" value={fmtMoney(grossSpread)} hint={grossSpreadHelp} />
              <Kv
                label="Price vs MMR"
                value={priceKey && priceRatio != null ? `${PRICE_LABEL[priceKey]} · ${Math.round(priceRatio * 100)}%` : '–'}
              />
            </div>
          </Card>

          {/* Market Insight (VMA) — display only, not scored (§14.5). Wholesale
              supply/demand/velocity also feed the urgency score; retail is context. */}
          {(vehicle.wholesale_supply_level || vehicle.wholesale_demand_level ||
            vehicle.retail_supply_level || vehicle.retail_demand_level ||
            vehicle.retail_rmi != null || vehicle.retail_spread != null) && (
            <Card title="Market Insight (VMA)" className="vin-card-wide">
              <div className="vin-levels">
                <LevelBadge label="WS Supply" level={vehicle.wholesale_supply_level} tones={WS_SUPPLY_TONE} hint={VMA_FIELD_HELP.wholesale_supply_level} />
                <LevelBadge label="WS Demand" level={vehicle.wholesale_demand_level} tones={WS_DEMAND_TONE} hint={VMA_FIELD_HELP.wholesale_demand_level} />
                <LevelBadge label="Retail Supply" level={vehicle.retail_supply_level} tones={RETAIL_SUPPLY_TONE} hint={VMA_FIELD_HELP.retail_supply_level} />
                <LevelBadge label="Retail Demand" level={vehicle.retail_demand_level} tones={RETAIL_DEMAND_TONE} hint={VMA_FIELD_HELP.retail_demand_level} />
              </div>
              <DaysBars
                bars={[
                  { key: 'self', label: 'This listing so far', value: vehicle.list_days, tone: 'self' },
                  { key: 'ws-dts', label: 'Wholesale days to sell', value: vehicle.wholesale_avg_days_to_sell, tone: 'wholesale', hint: VMA_FIELD_HELP.wholesale_avg_days_to_sell },
                  { key: 'ws-sup', label: 'Wholesale days supply', value: vehicle.wholesale_days_supply, tone: 'wholesale', hint: VMA_FIELD_HELP.wholesale_days_supply },
                  { key: 'rt-dts', label: 'Retail days to sell', value: vehicle.retail_avg_days_to_sell, tone: 'retail', hint: VMA_FIELD_HELP.retail_avg_days_to_sell },
                  { key: 'rt-sup', label: 'Retail days supply', value: vehicle.retail_days_supply, tone: 'retail', hint: VMA_FIELD_HELP.retail_days_supply },
                ]}
              />
              <VelocityCaption daysListed={vehicle.list_days} avgDaysToSell={vehicle.wholesale_avg_days_to_sell} />
              <div className="vin-kv-grid">
                <Kv label="WS Supply" value={fmtStr(vehicle.wholesale_supply_level)} hint={VMA_FIELD_HELP.wholesale_supply_level} />
                <Kv label="WS Demand" value={fmtStr(vehicle.wholesale_demand_level)} hint={VMA_FIELD_HELP.wholesale_demand_level} />
                <Kv label="WS Days-to-Sell" value={fmtInt(vehicle.wholesale_avg_days_to_sell)} hint={VMA_FIELD_HELP.wholesale_avg_days_to_sell} />
                <Kv label="WS Days Supply" value={fmtInt(vehicle.wholesale_days_supply)} hint={VMA_FIELD_HELP.wholesale_days_supply} />
                <Kv label="Buyer Coverage" value={fmtInt(vehicle.buyer_coverage)} hint={VMA_FIELD_HELP.buyer_coverage} />
                <Kv label="Retail Supply" value={fmtStr(vehicle.retail_supply_level)} hint={VMA_FIELD_HELP.retail_supply_level} />
                <Kv label="Retail Demand" value={fmtStr(vehicle.retail_demand_level)} hint={VMA_FIELD_HELP.retail_demand_level} />
                <Kv label="Retail Days-to-Sell" value={fmtInt(vehicle.retail_avg_days_to_sell)} hint={VMA_FIELD_HELP.retail_avg_days_to_sell} />
                <Kv label="Retail Days Supply" value={fmtInt(vehicle.retail_days_supply)} hint={VMA_FIELD_HELP.retail_days_supply} />
                <Kv label="Retail RMI" value={fmtMoney(vehicle.retail_rmi)} hint={VMA_FIELD_HELP.retail_rmi} />
                <Kv label="Retail Spread" value={fmtMoney(vehicle.retail_spread)} hint={VMA_FIELD_HELP.retail_spread} />
                <Kv label="Retail Retention" value={vehicle.retail_retention != null ? `${Math.round(vehicle.retail_retention * 100)}%` : '–'} hint={VMA_FIELD_HELP.retail_retention} />
                <Kv label="Retail List (avg)" value={fmtMoney(vehicle.retail_avg_list_price)} hint={VMA_FIELD_HELP.retail_avg_list_price} />
                <Kv label="WS Sales Eff." value={vehicle.wholesale_sales_efficiency != null ? `${Math.round(vehicle.wholesale_sales_efficiency)}%` : '–'} hint={VMA_FIELD_HELP.wholesale_sales_efficiency} />
                <Kv label="WS Sales Effect." value={vehicle.wholesale_sales_effectiveness != null ? `${Math.round(vehicle.wholesale_sales_effectiveness)}%` : '–'} hint={VMA_FIELD_HELP.wholesale_sales_effectiveness} />
              </div>
              <p style={{ fontSize: 11, color: 'var(--color-text-secondary, #777)', margin: '6px 2px 0' }}>
                {VMA_PANEL_NOTE_BROKER}
              </p>
            </Card>
          )}

          {/* Where this cohort actually trades. The broker had no view of this —
              it answers "is this unit even in the right channel/venue?". */}
          {vehicle.vin && (
            <Card title="Placement" className="vin-card-wide">
              <VinCohortInsightsCard
                vin={vehicle.vin}
                sections={['channels', 'venues']}
                insights={cohortInsights}
                currentAuction={vehicle.auction}
              />
            </Card>
          )}

          {/* Vehicle, listing & condition — the Client-View treatment (§6.3):
              one card, a summary anyone needs at a glance, and the long tail behind
              labelled expanders. Every field the three separate cards carried is
              still here. */}
          <Card title="Vehicle, listing & condition" className="vin-card-wide">
            <div className="vin-kv-grid">
              <Kv label="Year" value={fmtStr(vehicle.model_year)} />
              <Kv label="Make" value={fmtStr(vehicle.make)} />
              <Kv label="Model" value={fmtStr(vehicle.model)} />
              <Kv label="Trim" value={fmtStr(vehicle.trim)} />
              <Kv label="Mileage" value={fmtMileage(vehicle.mileage)} />
              <Kv label="Color" value={fmtStr(vehicle.color)} />
              <Kv label="Seller" value={fmtStr(vehicle.seller_name)} />
              <Kv label="List Status" value={fmtStr(vehicle.list_status)} />
              <Kv label="Days Listed" value={fmtInt(vehicle.list_days)} />
              <Kv label="Channel" value={fmtStr(vehicle.channel)} />
              <Kv label="Auction" value={fmtStr(vehicle.auction)} />
              <Kv
                label="VDP"
                value={
                  vehicle.vdp_url ? (
                    <a href={vehicle.vdp_url} target="_blank" rel="noopener noreferrer">Open VDP ↗</a>
                  ) : (
                    '–'
                  )
                }
              />
            </div>

            <Expander label="Full specs">
              <div className="vin-kv-grid">
                <Kv label="Body Style" value={fmtStr(vehicle.body_style)} />
                <Kv label="Doors" value={fmtStr(vehicle.doors)} />
                <Kv label="Drivetrain" value={fmtStr(vehicle.drivetrain)} />
                <Kv label="Engine" value={fmtStr(vehicle.engine)} />
                <Kv label="Fuel Type" value={fmtStr(vehicle.fuel_type)} />
                <Kv label="Transmission" value={fmtStr(vehicle.transmission)} />
                <Kv label="Vehicle Type" value={fmtStr(vehicle.vehicle_type)} />
                <Kv label="VIN" value={vehicle.vin} />
              </div>
            </Expander>

            <Expander label="Listing detail">
              <div className="vin-kv-grid">
                <Kv label="Seller #" value={fmtStr(vehicle.seller_id)} />
                <Kv
                  label="Seller Location"
                  value={fmtStr([vehicle.location_state ?? vehicle.state, vehicle.seller_zip_code || vehicle.zip_code].filter(Boolean).join(' · ') || null)}
                />
                <Kv label="List Time" value={fmtDate(vehicle.list_time)} />
                <Kv
                  label="Repriced / Updated"
                  value={vehicle.days_since_reprice == null ? '—' : `${vehicle.days_since_reprice}d ago`}
                />
                <Kv label="Facilitation" value={fmtStr(vehicle.facilitation)} />
                <Kv label="Site Location" value={fmtStr(vehicle.site_location)} />
                <Kv label="State" value={fmtStr(vehicle.location_state ?? vehicle.state)} />
                <Kv label="MMR Range" value={fmtStr(vehicle.mmr_range)} />
                <Kv label="VDP Views (avg/day)" value={fmtInt(vehicle.average_daily_vdp_views)} />
                <Kv label="Client VDP Views" value={fmtInt(vehicle.total_client_vdp_views)} />
                <Kv label="Workbook Saves" value={fmtInt(vehicle.client_workbook_saves)} />
                <Kv label="Photos" value={fmtInt(vehicle.photo_count)} />
                <Kv label="Acquired" value={fmtDate(vehicle.seller_acquisition_time)} />
                <Kv label="Ownership Days" value={fmtInt(vehicle.seller_ownership_days)} />
                <Kv label="Acquisition Method" value={fmtStr(vehicle.seller_acquisition_method)} />
                <Kv label="Listing Occurrences" value={fmtInt(vehicle.listing_occurences)} />
                <Kv
                  label="2nd-Chance Relist"
                  value={Number(vehicle.is_second_chance) === 1 ? 'Yes' : 'No'}
                />
              </div>
            </Expander>

            <Expander label="Inspection">
              <div className="vin-kv-grid">
                <Kv label="CR Grade" value={fmtCrGrade(vehicle.cr_grade)} />
                <Kv label="Inspect Status" value={fmtStr(vehicle.inspect_status)} />
                <Kv label="Inspect Time" value={fmtDate(vehicle.inspect_time)} />
                <Kv label="Inspection Channel" value={fmtStr(vehicle.inspection_channel)} />
                <Kv label="Inspection Source" value={fmtInspectionSource(vehicle.inspection_source)} />
                <Kv label="Inspection Type" value={fmtStr(vehicle.inspection_type)} />
                <Kv
                  label="Condition Report"
                  value={
                    vehicle.cr_link ? (
                      <a href={vehicle.cr_link} target="_blank" rel="noopener noreferrer">Open CR ↗</a>
                    ) : (
                      '–'
                    )
                  }
                />
              </div>
              {vehicle.cr_link && (
                <iframe
                  src={vehicle.cr_link}
                  title="Condition Report"
                  className="vin-cr-iframe"
                  sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                />
              )}
            </Expander>
          </Card>

          {/* Announcements. The condition flags are matched against the FUSED text,
              so the split below is shown for reading, never used to gate anything. */}
          {annc.full && (
            <Card title="Announcements">
              {annc.structured && (
                <div className="vin-annc-block vin-annc-structured">
                  <div className="vin-annc-label">Disclosures</div>
                  <div className="vin-annc-text">{annc.structured}</div>
                </div>
              )}
              {annc.remarks && (
                <div className="vin-annc-block">
                  <div className="vin-annc-label">Seller remarks</div>
                  <div className="vin-annc-text">{annc.remarks}</div>
                </div>
              )}
              {annc.dealer && (
                <div className="vin-annc-block">
                  <div className="vin-annc-label">Seller comments</div>
                  <div className="vin-annc-text">{annc.dealer}</div>
                </div>
              )}
              {!annc.split && (
                <div className="vin-annc-block">
                  <div className="vin-annc-label">
                    Full text <span className="vin-annc-note">— no reliable split for this listing</span>
                  </div>
                  <div className="vin-annc-text">{annc.full}</div>
                </div>
              )}
              {annc.split && (
                <Expander label="View full announcement text">
                  <div className="vin-annc-text">{annc.full}</div>
                </Expander>
              )}
            </Card>
          )}

          {/* Bids */}
          <Card title="Bids">
            <div className="vin-kv-grid">
              <Kv label="Active Bids" value={fmtInt(vehicle.active_bids)} />
              <Kv label="Total Bids" value={fmtInt(vehicle.total_bids)} />
              <Kv label="Active High Bid" value={fmtMoney(vehicle.active_high_bid)} />
            </div>
          </Card>

          {/* History tables (full width) */}
          {((vehicle.listing_history && vehicle.listing_history.length > 0) ||
            (vehicle.bid_history && vehicle.bid_history.length > 0)) && (
            <Card title="History" className="vin-card-wide">
              {vehicle.listing_history && vehicle.listing_history.length > 0 && (
                <Expander label={`Listing history (${vehicle.listing_history.length})`}>
                  <SortableTable
                    columns={listingHistoryCols}
                    rows={vehicle.listing_history as any[]}
                    searchPlaceholder="Search listing history"
                    rowKey={(r, i) => `${r.list_time}-${i}`}
                  />
                </Expander>
              )}
              {vehicle.bid_history && vehicle.bid_history.length > 0 && (
                <Expander label={`Bid history (${vehicle.bid_history.length})`}>
                  <SortableTable
                    columns={bidHistoryCols}
                    rows={vehicle.bid_history as any[]}
                    searchPlaceholder="Search bids"
                    rowKey={(r, i) => `${r.bid_time}-${i}`}
                  />
                </Expander>
              )}
            </Card>
          )}

          </div>
        </div>
      )}
    </div>
  );
};
